import api from './api';

export type SportCategoryName =
  | 'CRICKET'
  | 'VOLLEYBALL'
  | 'FOOTBALL'
  | 'KABADDI'
  | 'BADMINTON'
  | 'RUNNING'
  | 'TENNIS'
  | 'OTHER';

export type ExperienceLevel = 'BEGINNER' | 'LOCAL' | 'INTERMEDIATE' | 'ADVANCED';
export type GenderCategory = 'MALE' | 'FEMALE' | 'MIXED' | 'OPEN' | 'OTHER';
export type TeamStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUSPENDED';
export type TeamPlayerStatus = 'UNCLAIMED' | 'INVITED' | 'VERIFIED' | 'REJECTED' | 'REMOVED';
export type TournamentFormat =
  | 'KNOCKOUT'
  | 'LEAGUE'
  | 'ROUND_ROBIN'
  | 'SEMI_FINAL_FINAL'
  | 'BEST_OF_3'
  | 'FRIENDLY_SERIES'
  | 'ONE_DAY_TOURNAMENT'
  | 'WEEKEND_TOURNAMENT';
export type TournamentStatus =
  | 'DRAFT'
  | 'OPEN_FOR_REGISTRATION'
  | 'REGISTRATION_CLOSED'
  | 'FIXTURES_GENERATED'
  | 'ONGOING'
  | 'COMPLETED'
  | 'CANCELLED';
export type VerificationRequirement =
  | 'CAPTAIN_ONLY_VERIFICATION'
  | 'UNVERIFIED_PLAYERS_ALLOWED'
  | 'MINIMUM_VERIFIED_PLAYERS_REQUIRED'
  | 'ALL_PLAYERS_VERIFIED_REQUIRED';
export type MatchType = 'FRIENDLY' | 'TOURNAMENT' | 'PRACTICE' | 'TOURNAMENT_WARMUP';
export type ScoreStatus = 'NOT_SUBMITTED' | 'SUBMITTED' | 'OPPONENT_CONFIRMED' | 'DISPUTED' | 'VERIFIED' | 'REJECTED';

export interface SportsLocation {
  area: string;
  village?: string;
  mandal?: string;
  district?: string;
  state?: string;
  pincode?: string;
}

export interface PlayerSportPayload {
  sport: SportCategoryName;
  role: string;
  experienceLevel?: ExperienceLevel;
}

export interface RegisterPlayerPayload {
  name: string;
  phone: string;
  age?: number;
  gender?: GenderCategory;
  profilePhoto?: string;
  location: SportsLocation;
  availability?: string;
  sports: PlayerSportPayload[];
}

export interface ManualTeamPlayerPayload {
  playerName: string;
  phone?: string;
  role?: string;
  age?: number;
  jerseyNumber?: string;
  photo?: string;
  village?: string;
  registeredPlayerProfileId?: string;
  registeredUserId?: string;
  sendInvite?: boolean;
}

export interface ExistingSportsPlayer {
  id: string;
  userId?: string;
  name: string;
  phone?: string;
  age?: number;
  profilePhoto?: string;
  location?: SportsLocation;
  sportsInterested?: SportCategoryName[];
  sportProfiles?: Array<{
    sport: SportCategoryName;
    role?: string;
    experienceLevel?: ExperienceLevel;
  }>;
}

export interface TeamDetailsPayload {
  teamName: string;
  sport: SportCategoryName;
  location: SportsLocation;
  homeGround?: string;
  logo?: string;
  teamLevel?: ExperienceLevel;
  preferredMatchDays?: string[];
  preferredMatchTimings?: string[];
  description?: string;
}

export interface CreateTeamPayload {
  captain: {
    name: string;
    mobileNumber: string;
    otp?: string;
    age?: number;
    location: SportsLocation;
    profilePhoto?: string;
    alternatePhone?: string;
    addAsPlayer?: boolean;
  };
  team: TeamDetailsPayload;
  players?: ManualTeamPlayerPayload[];
}

export interface CreateTournamentPayload {
  name: string;
  sport: SportCategoryName;
  location: SportsLocation;
  groundId?: string;
  groundName?: string;
  startDate: string;
  endDate: string;
  registrationDeadline: string;
  format?: TournamentFormat;
  entryFee?: number;
  prizeDetails?: string;
  maxTeams: number;
  minPlayersPerTeam?: number;
  maxPlayersPerTeam?: number;
  ageCategory?: string;
  genderCategory?: GenderCategory;
  verificationRequirement?: VerificationRequirement;
  minimumVerifiedPlayers?: number;
  rules: string;
  organizerContact?: string;
  sponsorDetails?: string;
  status?: TournamentStatus;
}

export interface CreateChallengePayload {
  sport: SportCategoryName;
  teamAId: string;
  teamBId: string;
  matchDate: string;
  matchTime?: string;
  venue?: string;
  matchType?: MatchType;
  oversSetsOrDuration?: string;
  entryContribution?: number;
  officialRequired?: boolean;
  equipmentResponsibility?: string;
  rules?: string;
}

export interface SubmitScorePayload {
  scoreSummary: string;
  winnerTeamId?: string;
  isDraw?: boolean;
  resultSummary?: string;
  manOfTheMatchTeamPlayerId?: string;
}

export interface CreateGroundPayload {
  groundName: string;
  location: SportsLocation;
  supportedSports: SportCategoryName[];
  availableTimings?: string[];
  rentPerMatch?: number;
  rentPerHour?: number;
  lightsAvailable?: boolean;
  waterAvailable?: boolean;
  parkingAvailable?: boolean;
  washroomAvailable?: boolean;
  contactNumber: string;
  photos?: string[];
  ownerDetails?: string;
}

export interface CreateSponsorPayload {
  sponsorName: string;
  contactPerson: string;
  phone: string;
  businessType: string;
  logo?: string;
  tournamentId?: string;
  sponsorshipType?: 'BANNER' | 'TITLE_SPONSOR' | 'CO_SPONSOR' | 'PRIZE_SPONSOR';
  amount?: number;
}

const sportsService = {
  overview: async (params?: Record<string, string>) => {
    const response = await api.get('/sports/overview', { params });
    return response.data;
  },
  categories: async () => {
    const response = await api.get('/sports/categories');
    return response.data;
  },
  registerPlayer: async (payload: RegisterPlayerPayload) => {
    const response = await api.post('/sports/players/register', payload);
    return response.data;
  },
  myPlayerProfile: async () => {
    const response = await api.get('/sports/players/me');
    return response.data;
  },
  searchPlayers: async (params: { q?: string; phone?: string; sport?: SportCategoryName }) => {
    const response = await api.get('/sports/players/search', { params });
    return response.data as ExistingSportsPlayer[];
  },
  claimProfile: async (payload: { teamPlayerId?: string; teamId?: string; phone?: string; playerName?: string; requestedRole?: string }) => {
    const response = await api.post('/sports/players/claim-profile', payload);
    return response.data;
  },
  playerPerformance: async (playerId: string) => {
    const response = await api.get(`/sports/players/${playerId}/performance`);
    return response.data;
  },
  createTeam: async (payload: CreateTeamPayload) => {
    const response = await api.post('/sports/teams/create-with-captain', payload);
    return response.data;
  },
  teams: async (params?: Record<string, string>) => {
    const response = await api.get('/sports/teams', { params });
    return response.data;
  },
  nearbyTeams: async (params?: Record<string, string>) => {
    const response = await api.get('/sports/teams/nearby', { params });
    return response.data;
  },
  myTeams: async () => {
    const response = await api.get('/sports/teams/my');
    return response.data;
  },
  team: async (id: string) => {
    const response = await api.get(`/sports/teams/${id}`);
    return response.data;
  },
  myTeam: async (id: string) => {
    const response = await api.get(`/sports/teams/my/${id}`);
    return response.data;
  },
  addTeamPlayers: async (teamId: string, players: ManualTeamPlayerPayload[]) => {
    const response = await api.post(`/sports/teams/${teamId}/players`, { players });
    return response.data;
  },
  invitePlayer: async (teamId: string, teamPlayerId: string) => {
    const response = await api.post(`/sports/teams/${teamId}/invite-player`, { teamPlayerId });
    return response.data;
  },
  createTournament: async (payload: CreateTournamentPayload) => {
    const response = await api.post('/sports/tournaments', payload);
    return response.data;
  },
  tournaments: async (params?: Record<string, string>) => {
    const response = await api.get('/sports/tournaments', { params });
    return response.data;
  },
  tournament: async (id: string) => {
    const response = await api.get(`/sports/tournaments/${id}`);
    return response.data;
  },
  registerTeamForTournament: async (tournamentId: string, payload: { teamId: string; selectedTeamPlayers?: string[] }) => {
    const response = await api.post(`/sports/tournaments/${tournamentId}/register-team`, payload);
    return response.data;
  },
  generateFixtures: async (tournamentId: string, payload?: { fixtures?: any[] }) => {
    const response = await api.post(`/sports/tournaments/${tournamentId}/generate-fixtures`, payload || {});
    return response.data;
  },
  matches: async (params?: Record<string, string>) => {
    const response = await api.get('/sports/matches', { params });
    return response.data;
  },
  createChallenge: async (payload: CreateChallengePayload) => {
    const response = await api.post('/sports/matches/challenge', payload);
    return response.data;
  },
  submitScore: async (matchId: string, payload: SubmitScorePayload) => {
    const response = await api.post(`/sports/matches/${matchId}/submit-score`, payload);
    return response.data;
  },
  confirmScore: async (matchId: string) => {
    const response = await api.post(`/sports/matches/${matchId}/confirm-score`);
    return response.data;
  },
  disputeScore: async (matchId: string) => {
    const response = await api.post(`/sports/matches/${matchId}/dispute-score`);
    return response.data;
  },
  grounds: async (params?: Record<string, string>) => {
    const response = await api.get('/sports/grounds', { params });
    return response.data;
  },
  createGround: async (payload: CreateGroundPayload) => {
    const response = await api.post('/sports/grounds', payload);
    return response.data;
  },
  sponsors: async () => {
    const response = await api.get('/sports/sponsors');
    return response.data;
  },
  createSponsor: async (payload: CreateSponsorPayload) => {
    const response = await api.post('/sports/sponsors', payload);
    return response.data;
  },
  adminOverview: async () => {
    const response = await api.get('/admin/sports/overview');
    return response.data;
  },
  adminTeams: async () => {
    const response = await api.get('/admin/sports/teams');
    return response.data;
  },
  approveTeam: async (id: string) => {
    const response = await api.put(`/admin/sports/teams/${id}/approve`);
    return response.data;
  },
  rejectTeam: async (id: string) => {
    const response = await api.put(`/admin/sports/teams/${id}/reject`);
    return response.data;
  },
  adminTournaments: async () => {
    const response = await api.get('/admin/sports/tournaments');
    return response.data;
  },
  approveTournament: async (id: string) => {
    const response = await api.put(`/admin/sports/tournaments/${id}/approve`);
    return response.data;
  },
  rejectTournament: async (id: string) => {
    const response = await api.put(`/admin/sports/tournaments/${id}/reject`);
    return response.data;
  },
  adminMatches: async () => {
    const response = await api.get('/admin/sports/matches');
    return response.data;
  },
  verifyScore: async (id: string, scoreStatus: ScoreStatus = 'VERIFIED') => {
    const response = await api.put(`/admin/sports/matches/${id}/verify-score`, { scoreStatus });
    return response.data;
  },
};

export default sportsService;
